import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Button,
  Checkbox,
  Field,
  Input,
  Modal,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { bulkEditFormToBody, useBulkUpdateVariantsMutation, useVariantsQuery } from '../api';
import { bulkEditFormSchema, type BulkEditFormValues } from '../schemas';

import { useToast } from '@/components/ui/toast/ToastProvider';
import { whenDefined } from '@/lib/optional-props';

const DEFAULT_VALUES: BulkEditFormValues = {
  priceMode: 'none',
  isActive: 'unchanged',
};

/**
 * Variant-improvements lot (item 5) — bulk price/stock/threshold/status
 * edit across an explicit selection of variants, atomic on the server
 * (`ProductVariantRepository.bulkUpdate()`'s own guarantee: one variant's
 * refused stock delta rolls back the whole batch, never a partial
 * apply). This component only ever sends `selector: { variantIds }` — the
 * `optionValueIds` selector `bulkUpdateVariantsInputSchema` also accepts
 * has no UI here yet (an explicit checklist covers the same ground for a
 * merchant's own product-level catalogue, and inventing a second,
 * redundant selection control was not asked for).
 */
export function BulkEditGrid({ productId }: { productId: string }): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const variantsQuery = useVariantsQuery(productId);
  const bulkUpdateMutation = useBulkUpdateVariantsMutation(productId);

  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<BulkEditFormValues>({
    resolver: zodResolver(bulkEditFormSchema),
    defaultValues: DEFAULT_VALUES,
  });
  const priceMode = watch('priceMode');

  const variants = variantsQuery.data ?? [];
  const allSelected = variants.length > 0 && selectedIds.size === variants.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  function toggleAll(): void {
    setSelectedIds(allSelected ? new Set() : new Set(variants.map((v) => v.id)));
  }

  function toggleOne(variantId: string): void {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(variantId)) next.delete(variantId);
      else next.add(variantId);
      return next;
    });
  }

  function close(): void {
    setOpen(false);
    setSelectedIds(new Set());
    reset(DEFAULT_VALUES);
  }

  async function onSubmit(values: BulkEditFormValues): Promise<void> {
    if (selectedIds.size === 0) return;
    try {
      const result = await bulkUpdateMutation.mutateAsync(
        bulkEditFormToBody({ variantIds: [...selectedIds] }, values),
      );
      toast.success(t('merchant.products.bulkEdit.appliedToast', { count: result.updated }));
      close();
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    }
  }

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('merchant.products.bulkEdit.openButton')}
      </Button>

      <Modal
        open={open}
        title={t('merchant.products.bulkEdit.title')}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              {t('merchant.common.cancel')}
            </Button>
            <Button
              type="submit"
              form="bulk-edit-form"
              loading={bulkUpdateMutation.isPending}
              disabled={selectedIds.size === 0}
            >
              {t('merchant.products.bulkEdit.apply')}
            </Button>
          </>
        }
      >
        <form
          id="bulk-edit-form"
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
          noValidate
          className="flex flex-col gap-4"
        >
          <p className="text-sm text-inkFaint">
            {selectedIds.size === 0
              ? t('merchant.products.bulkEdit.noSelection')
              : t('merchant.products.bulkEdit.selectedCount', { count: selectedIds.size })}
          </p>

          <Table caption={t('merchant.products.bulkEdit.title')}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>
                  <Checkbox
                    label={t('merchant.products.bulkEdit.selectAll')}
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                  />
                </TableHeaderCell>
                <TableHeaderCell>{t('merchant.products.form.skuLabel')}</TableHeaderCell>
                <TableHeaderCell>{t('merchant.products.variants.colAttributes')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variants.map((variant) => (
                <TableRow key={variant.id}>
                  <TableCell>
                    <Checkbox
                      label={variant.sku}
                      checked={selectedIds.has(variant.id)}
                      onChange={() => {
                        toggleOne(variant.id);
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-inkSoft">{variant.sku}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-inkSoft">
                      {Object.entries(variant.attributes)
                        .map(([key, value]) => `${key}: ${String(value)}`)
                        .join(', ') || '—'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Controller
            control={control}
            name="priceMode"
            render={({ field }) => (
              <RadioGroup
                legend={t('merchant.products.bulkEdit.priceModeLabel')}
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                options={[
                  { value: 'none', label: t('merchant.products.bulkEdit.priceMode.none') },
                  { value: 'relative', label: t('merchant.products.bulkEdit.priceMode.relative') },
                  { value: 'absolute', label: t('merchant.products.bulkEdit.priceMode.absolute') },
                ]}
              />
            )}
          />
          {priceMode !== 'none' ? (
            <Field
              label={t('merchant.products.bulkEdit.priceAmountLabel')}
              {...whenDefined('error', errors.priceAmount?.message)}
            >
              {(bind) => (
                <Input
                  {...bind}
                  type="number"
                  step="0.001"
                  {...register('priceAmount', { valueAsNumber: true })}
                />
              )}
            </Field>
          ) : null}

          <Field
            label={t('merchant.products.bulkEdit.stockDeltaLabel')}
            optional
            hint={t('merchant.products.bulkEdit.stockDeltaHint')}
            {...whenDefined('error', errors.stockDelta?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                type="number"
                step="1"
                {...register('stockDelta', { valueAsNumber: true })}
              />
            )}
          </Field>

          <Field
            label={t('merchant.products.bulkEdit.thresholdLabel')}
            optional
            {...whenDefined('error', errors.quantityMinThreshold?.message)}
          >
            {(bind) => (
              <Input
                {...bind}
                type="number"
                step="1"
                min="0"
                {...register('quantityMinThreshold', { valueAsNumber: true })}
              />
            )}
          </Field>

          <Controller
            control={control}
            name="isActive"
            render={({ field }) => (
              <RadioGroup
                legend={t('merchant.products.bulkEdit.statusLabel')}
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                options={[
                  { value: 'unchanged', label: t('merchant.products.bulkEdit.status.unchanged') },
                  { value: 'activate', label: t('merchant.products.bulkEdit.status.activate') },
                  { value: 'deactivate', label: t('merchant.products.bulkEdit.status.deactivate') },
                ]}
              />
            )}
          />

          <Field label={t('merchant.products.bulkEdit.reasonLabel')} optional>
            {(bind) => <Input {...bind} {...register('reason')} />}
          </Field>

          {errors.priceMode !== undefined ? (
            <p role="alert" className="text-sm text-critical">
              {errors.priceMode.message}
            </p>
          ) : null}
        </form>
      </Modal>
    </>
  );
}
